import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Edit, Trash2, DollarSign, Calendar, Building2, FileText, Banknote, Smartphone, CreditCard, User, RotateCcw, Plus, Clock, CheckCircle, Printer, Mail, IndianRupee } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { toast } from 'react-hot-toast';
import { paymentsMadeAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const PAYMENT_MODE_CONFIG = {
  cash: { icon: Banknote, label: 'Cash', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  upi: { icon: Smartphone, label: 'UPI', bg: 'bg-teal-100', text: 'text-teal-700' },
  bank_transfer: { icon: Building2, label: 'Bank Transfer', bg: 'bg-blue-100', text: 'text-blue-700' },
  card: { icon: CreditCard, label: 'Card', bg: 'bg-amber-100', text: 'text-amber-700' },
  cheque: { icon: FileText, label: 'Cheque', bg: 'bg-slate-100', text: 'text-slate-700' },
  wallet: { icon: DollarSign, label: 'Wallet', bg: 'bg-cyan-100', text: 'text-cyan-700' },
  other: { icon: DollarSign, label: 'Other', bg: 'bg-gray-100', text: 'text-gray-700' }
};

const PAYMENT_STATUS_CONFIG = {
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Completed', icon: CheckCircle },
  draft: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Draft', icon: Clock },
  cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled', icon: RotateCcw },
  voided: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled', icon: RotateCcw }
};

const BILL_STATUS_CONFIG = {
  unpaid: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Unpaid' },
  partially_paid: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Partially Paid' },
  paid: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Paid' },
  overdue: { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue' }
};

const PaymentMadeDetailPage = () => {
  const { businessId, id: paymentId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const bid = businessId || activeBusiness?.id;

  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  useEffect(() => {
    fetchPayment();
  }, [paymentId]);

  const fetchPayment = async () => {
    try {
      const response = await paymentsMadeAPI.get(paymentId);
      setPayment(response.data);
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to load payment')); }
    setLoading(false);
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) { toast.error('Please enter reason'); return; }
    try {
      await paymentsMadeAPI.void(paymentId, cancelReason);
      toast.success('Payment cancelled');
      fetchPayment();
      setShowCancelModal(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel');
    }
  };

  const downloadVoucher = async () => {
    try {
      const res = await paymentsMadeAPI.pdf(paymentId);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Payment_${payment?.payment_no}.pdf`;
      a.click();
      toast.success('Voucher downloaded');
    } catch (e) { toast.error('Download failed'); }
  };

  const handleBack = () => {
    navigate(`/business/${bid}/payments-made`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-[16px] text-slate-600 font-medium">Payment not found</p>
          <button onClick={handleBack} className="mt-4 text-teal-600 hover:underline text-[14px]">
            ← Back to Payments Made
          </button>
        </div>
      </div>
    );
  }

  const modeConfig = PAYMENT_MODE_CONFIG[payment.payment_mode] || PAYMENT_MODE_CONFIG.other;
  const ModeIcon = modeConfig.icon;
  const statusConfig = PAYMENT_STATUS_CONFIG[payment.status] || PAYMENT_STATUS_CONFIG.completed;
  const StatusIcon = statusConfig.icon;
  
  const isAdvance = !(payment.allocations || payment.invoices_allocated || []) || (payment.allocations || payment.invoices_allocated || []).length === 0;
  const linkedBill = (payment.allocations || payment.invoices_allocated || [])?.[0];
  const billTotal = linkedBill?.invoice_total || 0;
  const paidForThisBill = linkedBill?.allocated_amount || 0;
  const billBalanceLeftAfter = linkedBill ? Math.max(0, (linkedBill.balance_before || 0) - paidForThisBill) : 0;
  const billPaidSoFar = billTotal - billBalanceLeftAfter;
  
  let billStatusAfter = 'partially_paid';
  if (billBalanceLeftAfter <= 0) billStatusAfter = 'paid';
  else if (billPaidSoFar <= 0) billStatusAfter = 'unpaid';
  const billStatusConfig = BILL_STATUS_CONFIG[billStatusAfter];
  
  const extraAdvance = Math.max(0, (payment.amount_paid || 0) - paidForThisBill);

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* PREMIUM HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Back + Payment Info */}
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-[20px] font-bold text-slate-800">{payment.payment_no || payment.payment_number}</h1>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold ${statusConfig.bg} ${statusConfig.text}`}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    {statusConfig.label}
                  </span>
                </div>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  Payment Voucher • {formatDate(payment.payment_date)}
                </p>
              </div>
            </div>

            {/* Right: Action Buttons */}
            <div className="flex items-center gap-2">
              {payment.status === 'completed' && (
                <>
                  <button
                    onClick={() => navigate(`/business/${bid}/payments-made/${paymentId}/edit`)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2.5 text-red-600 bg-white border border-red-300 rounded-xl hover:bg-red-50 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Cancel
                  </button>
                </>
              )}
              <div className="w-px h-8 bg-slate-200 mx-1"></div>
              <button onClick={downloadVoucher} className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98]" title="Download Voucher">
                <Download className="w-4 h-4" />
              </button>
              <button className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98]" title="Print">
                <Printer className="w-4 h-4" />
              </button>
              <button className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98]" title="Send Email">
                <Mail className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT - Premium 3-Column Layout */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - Supplier & Payment Info (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Supplier Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <Building2 className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Supplier</h2>
                </div>
                
                {payment.supplier_name || payment.party_name ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-teal-100 flex items-center justify-center">
                        <span className="text-[16px] font-bold text-teal-700">
                          {payment.supplier_name || payment.party_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">{payment.supplier_name || payment.party_name}</p>
                        <p className="text-[12px] text-slate-500">Supplier</p>
                      </div>
                    </div>
                    
                    {payment.supplier_phone && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <User className="w-4 h-4 text-slate-400" />
                        {payment.supplier_phone}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-[13px] text-slate-500">No supplier selected</p>
                  </div>
                )}
              </div>

              {/* Payment Info Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Payment Info</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Payment Date</span>
                    <span className="text-[13px] font-medium text-slate-700">{formatDate(payment.payment_date)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Payment Mode</span>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${modeConfig.bg} ${modeConfig.text}`}>
                      <ModeIcon className="w-3 h-3" />
                      {modeConfig.label}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Paid From</span>
                    <span className="text-[13px] font-medium text-slate-700">{payment.debit_from_account || 'Cash'}</span>
                  </div>
                  {payment.paid_by_name && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Paid By</span>
                      <span className="text-[13px] font-medium text-slate-700">{payment.paid_by_name}</span>
                    </div>
                  )}
                  {(payment.transaction_reference || payment.reference_number || payment.transaction_id) && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Reference #</span>
                      <span className="text-[13px] font-medium text-slate-700">{(payment.transaction_reference || payment.reference_number || payment.transaction_id)}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Cancelled Info */}
              {(payment.status === 'voided' || payment.status === 'cancelled') && (
                <div className="bg-red-50 rounded-2xl p-5 shadow-sm border border-red-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-sm">
                      <RotateCcw className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-[14px] font-semibold text-red-800">Cancelled</h2>
                  </div>
                  <p className="text-[13px] text-red-700">
                    <span className="font-semibold">Reason:</span> {payment.void_reason || 'No reason provided'}
                  </p>
                  {payment.voided_at && (
                    <p className="text-[11px] text-red-600 mt-2">
                      Cancelled on {formatDate(payment.voided_at)}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* CENTER COLUMN - Bill Details (6 cols) */}
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Payment Allocation</h2>
                      <p className="text-[12px] text-slate-500">{isAdvance ? 'Advance Payment' : 'Bill Payment'}</p>
                    </div>
                  </div>
                </div>
                
                {isAdvance ? (
                  <div className="p-8 text-center bg-amber-50">
                    <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                      <DollarSign className="w-8 h-8 text-amber-500" />
                    </div>
                    <p className="text-amber-700 font-bold text-xl">Extra Paid (Advance)</p>
                    <p className="text-sm text-amber-600 mt-2">This payment was not linked to any bill</p>
                    <p className="text-3xl font-bold text-amber-600 mt-4">₹{(payment.amount_paid || 0).toLocaleString('en-IN')}</p>
                    <p className="text-xs text-amber-500 mt-2">Available as advance for future bills</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Bill #</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Bill Total</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Paid So Far</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-teal-600 uppercase tracking-wide">This Payment</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Balance Left</th>
                          <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        <tr className="hover:bg-slate-50/50 transition-colors duration-150">
                          <td className="px-5 py-4">
                            <p 
                              className="text-[14px] font-bold text-blue-600 hover:underline cursor-pointer" 
                              onClick={() => navigate(`/business/${bid}/purchase-invoices/${linkedBill?.invoice_id}`)}
                            >
                              {linkedBill?.invoice_number}
                            </p>
                            {linkedBill?.supplier_invoice_no && (
                              <p className="text-[11px] text-slate-400 mt-0.5">Vendor: {linkedBill.supplier_invoice_no}</p>
                            )}
                            {linkedBill?.invoice_date && (
                              <p className="text-[11px] text-slate-400 mt-0.5">{formatDate(linkedBill.invoice_date)}</p>
                            )}
                          </td>
                          <td className="px-5 py-4 text-right">
                            <span className="text-[14px] font-semibold text-slate-800">₹{billTotal.toLocaleString('en-IN')}</span>
                          </td>
                          <td className="px-5 py-4 text-right">
                            <span className="text-[14px] font-semibold text-emerald-600">₹{billPaidSoFar.toLocaleString('en-IN')}</span>
                          </td>
                          <td className="px-5 py-4 text-right">
                            <span className="text-[16px] font-bold text-teal-600">₹{paidForThisBill.toLocaleString('en-IN')}</span>
                          </td>
                          <td className="px-5 py-4 text-right">
                            <span className={`text-[14px] font-bold ${billBalanceLeftAfter > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                              ₹{billBalanceLeftAfter.toLocaleString('en-IN')}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-center">
                            <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${billStatusConfig?.bg} ${billStatusConfig?.text}`}>
                              {billStatusConfig?.label}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    
                    {/* Extra Advance Section */}
                    {extraAdvance > 0 && (
                      <div className="px-5 py-4 bg-amber-50 border-t border-amber-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[14px] font-semibold text-amber-700">Extra Paid (Advance)</p>
                            <p className="text-[12px] text-amber-600">Available for future bills from this supplier</p>
                          </div>
                          <p className="font-bold text-amber-600 text-xl">₹{extraAdvance.toLocaleString('en-IN')}</p>
                        </div>
                      </div>
                    )}
                    
                    {/* Add Payment Button */}
                    {billBalanceLeftAfter > 0 && payment.status === 'completed' && (
                      <div className="px-5 py-4 bg-slate-50 border-t border-slate-200">
                        <button
                          onClick={() => navigate(`/business/${bid}/payments-made/new?supplier_id=${payment.supplier_id || payment.party_id}&invoice_id=${linkedBill?.invoice_id}`)}
                          className="w-full py-3 text-sm font-semibold text-teal-600 bg-teal-100 hover:bg-teal-200 rounded-xl flex items-center justify-center gap-2 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Add Payment (₹{billBalanceLeftAfter.toLocaleString('en-IN')} remaining)
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {payment.notes && (
                  <div className="px-5 py-4 bg-amber-50 border-t border-amber-100">
                    <p className="text-[11px] text-amber-600 uppercase tracking-wide font-semibold mb-1">Notes</p>
                    <p className="text-[13px] text-amber-800">{payment.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN - Summary (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Summary Card - Premium Teal */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-5 shadow-sm border border-teal-200/60">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-sm">
                    <IndianRupee className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Payment Summary</h2>
                </div>
                
                <div className="space-y-3">
                  {linkedBill && (
                    <>
                      <div className="flex justify-between text-[13px]">
                        <span className="text-slate-600">Paid For Bill</span>
                        <span className="text-slate-800 font-medium">₹{paidForThisBill.toLocaleString('en-IN')}</span>
                      </div>
                      
                      {extraAdvance > 0 && (
                        <div className="flex justify-between text-[13px]">
                          <span className="text-amber-600">Extra (Advance)</span>
                          <span className="text-amber-600 font-medium">+ ₹{extraAdvance.toLocaleString('en-IN')}</span>
                        </div>
                      )}
                    </>
                  )}
                  
                  <div className="border-t border-teal-200 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Total Paid</span>
                      <span className="text-[22px] font-bold text-teal-600">
                        ₹{(payment.amount_paid || 0).toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Timeline Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Status</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${payment.status === 'completed' ? 'bg-emerald-500' : payment.status === 'draft' ? 'bg-amber-500' : 'bg-red-500'}`}></div>
                    <div>
                      <p className="text-[13px] font-medium text-slate-700">{statusConfig.label}</p>
                      <p className="text-[11px] text-slate-500">{formatDate(payment.payment_date)}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <h2 className="text-[14px] font-semibold text-slate-800 mb-4">Quick Actions</h2>
                <div className="space-y-2">
                  {linkedBill && (
                    <button
                      onClick={() => navigate(`/business/${bid}/purchase-invoices/${linkedBill.invoice_id}`)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors duration-200"
                    >
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span className="text-[13px] font-medium text-blue-700">View Bill & History</span>
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/business/${bid}/parties/${payment.supplier_id || payment.party_id}`)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors duration-200"
                  >
                    <Building2 className="w-4 h-4 text-slate-600" />
                    <span className="text-[13px] font-medium text-slate-700">View Supplier Ledger</span>
                  </button>
                  <button
                    onClick={downloadVoucher}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-teal-50 hover:bg-teal-100 transition-colors duration-200"
                  >
                    <Download className="w-4 h-4 text-teal-600" />
                    <span className="text-[13px] font-medium text-teal-700">Download Voucher</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Cancel Payment</h3>
            <p className="text-sm text-slate-500 mb-4">This will reverse the payment and restore the bill balance.</p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Enter reason for cancellation..."
              rows={3}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-red-500/20"
            />
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setShowCancelModal(false)} className="px-5 py-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg text-sm font-medium">Keep Payment</button>
              <button onClick={handleCancel} className="px-5 py-2.5 bg-red-600 text-white hover:bg-red-700 rounded-lg text-sm font-medium">Cancel Payment</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMadeDetailPage;